import React, { useState, useEffect } from 'react';
import { TeamData, Team, Positional, StarPlayerData, Staff, Inducement } from '../types';

import starPlayersData from '../data/star_players.json';
import { TeamMakeup } from '../components/TeamMakeup';
import { PlayerCardModal } from '../components/PlayerCardModal';
import { StaffModal } from '../components/StaffModal';
import { TreasuryModal } from '../components/TreasuryModal';
import { StarPlayerModal, StarPlayer } from '../components/StarPlayerModal';
import './RosterPage.css';

interface RosterTabProps {
  team: Team;
  onChange: (updatedTeam: Team) => void;
}

export default function RosterTab({ team, onChange }: RosterTabProps) {
  const [positionals, setPositionals] = useState<Positional[]>([]);
  const [teamData, setTeamData] = useState<TeamData | null>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'hire' | 'view'>('hire');
  const [selectedPositional, setSelectedPositional] = useState<Positional | null>(null);
  const [selectedPlayerIndex, setSelectedPlayerIndex] = useState<number | null>(null);

  // Staff modal state and inducements 
  const [staffModalItem, setStaffModalItem] = useState<Staff | null>(null);
  const [inducementModalItem, setInducementModalItem] = useState<Inducement | null>(null);
  const [isTreasuryModalOpen, setIsTreasuryModalOpen] = useState(false);
  const [selectedStarPlayerForModal, setSelectedStarPlayerForModal] = useState<StarPlayer | null>(null);

  // Active configurations with 2025 defaults
  const rulesetMode = team.rulesetMode || 'league';
  const goldBudget = team.goldBudget !== undefined ? team.goldBudget : 1000000;

  // Collapsible details panels states
  const [isRosterCollapsed, setIsRosterCollapsed] = useState(false);
  const [isStaffCollapsed, setIsStaffCollapsed] = useState(false);
  const [isInducementsCollapsed, setIsInducementsCollapsed] = useState(false);
  const [isStarPlayersCollapsed, setIsStarPlayersCollapsed] = useState(false);



  // Formatter for currency/cost values (e.g. 120000 -> 120,000)
  const formatCost = (cost: string | number | null | undefined): string => {
    if (cost === null || cost === undefined) return '0';
    const num = typeof cost === 'number' ? cost : parsePlayerCost(String(cost));
    return num.toLocaleString('en-US');
  };

  // Helper to parse strings like "110K" or "50K" to numeric values 110,000 / 50,000
  const parsePlayerCost = (costStr: string | undefined): number => {
    if (!costStr) return 0;
    const clean = costStr.toUpperCase().replace(/,/g, '').trim();
    if (clean.endsWith('K')) {
      return (parseFloat(clean.slice(0, -1)) || 0) * 1000;
    }
    const parsed = parseInt(clean, 10);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Dynamic cost mathematical reductions
  const playersCost = team.players.reduce((sum, player) => {
    if (!player.position || !player.stats?.Cost) return sum;
    return sum + parsePlayerCost(player.stats.Cost);
  }, 0);

  const staffCounts = team.staffCounts || {};
  const inducementCounts = team.inducementCounts || {};
  const selectedStarPlayers = team.selectedStarPlayers || {};

  const staffCost = teamData?.Staff
    ? teamData.Staff.reduce((sum, staff) => {
      const count = staffCounts[staff.name] || 0;
      return sum + count * (staff.cost || 0);
    }, 0)
    : 0;

  const inducementsCost = (rulesetMode === 'exhibition' && teamData?.Inducements)
    ? teamData.Inducements.reduce((sum, inducement) => {
      const count = inducementCounts[inducement.name] || 0;
      return sum + count * (inducement.cost || 0);
    }, 0)
    : 0;

  // Filter eligible star players dynamically based on team's leagues
  const eligibleStarPlayers = (teamData && teamData.Leagues)
    ? (starPlayersData as StarPlayerData[]).filter((star) => {
        const targetLeagues = team.selectedLeague 
          ? [team.selectedLeague.toLowerCase()] 
          : teamData.Leagues.map((l: { name: string }) => l.name.toLowerCase());
          
        return star.plays_for && star.plays_for.some((league: string) => {
          const lowerLeague = league.toLowerCase();
          return lowerLeague === 'any team' || targetLeagues.includes(lowerLeague);
        });
      }).sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const starPlayersCost = rulesetMode === 'exhibition'
    ? eligibleStarPlayers.reduce((sum: number, star) => {
        const isSelected = selectedStarPlayers[star.name] || false;
        return sum + (isSelected ? star.Cost : 0);
      }, 0)
    : 0;

  const hiredStarPlayers = rulesetMode === 'exhibition'
    ? eligibleStarPlayers.filter((sp) => selectedStarPlayers[sp.name])
    : [];

  const currentRosterCount = team.players.filter(p => p.position).length + hiredStarPlayers.length;

  const totalSpent = playersCost + staffCost + inducementsCost + starPlayersCost;
  // Treasury is source-of-truth when present. For legacy teams without a treasury field,
  // fall back to derived value until the next draft action writes treasury explicitly.
  const treasury = team.treasury !== undefined ? team.treasury : (goldBudget - totalSpent);

  // Positional quantity limit helpers
  const getPositionCount = (positionName: string, excludeIndex?: number): number => {
    return team.players.reduce((count, p, idx) => {
      if (idx === excludeIndex) return count;
      return p.position === positionName ? count + 1 : count;
    }, 0);
  };

  const getMaxLimit = (qtyStr: string | undefined): number => {
    if (!qtyStr) return 0;
    const parts = qtyStr.split('-');
    const maxPart = parts[parts.length - 1];
    const max = parseInt(maxPart, 10);
    return isNaN(max) ? 0 : max;
  };

  const isPositionFull = (positionName: string, currentPlayerIndex: number): boolean => {
    const positional = positionals.find((p) => p.Position === positionName);
    if (!positional) return false;
    const count = getPositionCount(positionName, currentPlayerIndex);
    const max = getMaxLimit(positional.Qty);
    return count >= max;
  };

  // Extract parenthetical keywords e.g. "Bretonnian Squire (Lineman, Human)" -> "Lineman, Human"
  const getPositionKeywords = (positionName: string): string => {
    if (!positionName) return '';
    const positional = positionals.find((p) => p.Position === positionName);
    if (!positional) return '';
    const match = positional.Position.match(/\(([^)]+)\)/);
    return match ? match[1] : '';
  };

  // Load team data whenever team type changes
  useEffect(() => {
    if (!team.region || team.region === 'Unknown Region') {
      setPositionals([]);
      setTeamData(null);
      return;
    }

    const factionName = team.region;

    import(`../data/${factionName.replace(/\s+/g, '')}.json`)
      .then((data: any) => {
        setTeamData(data);
        setPositionals(data.Positionals || []);

        setErrorMessage(null);
      })
      .catch((err) => {
        console.error(`Failed to load data for team template: ${factionName}`, err);
        setErrorMessage(`Could not load roster templates for race: ${factionName}`);
      });
  }, [team.teamName, team.region]);





  const handleHirePlayer = (quantity: number = 1) => {
    if (!selectedPositional) return;

    const updatedPlayers = [...team.players];
    const positionName = selectedPositional.Position;
    const cleanName = positionName.split(' (')[0];

    let hiredCount = 0;
    for (let q = 0; q < quantity; q++) {
      const emptySlotIndex = updatedPlayers.findIndex(p => !p.position);
      if (emptySlotIndex === -1) break;

      const currentCount = updatedPlayers.filter(p => p.position === positionName).length;
      const defaultName = `${cleanName} #${currentCount + 1}`;

      updatedPlayers[emptySlotIndex] = {
        ...updatedPlayers[emptySlotIndex],
        position: positionName,
        name: defaultName,
        stats: {
          MA: selectedPositional.MA ?? '',
          ST: selectedPositional.ST ?? '',
          AG: selectedPositional.AG ?? '',
          PA: selectedPositional.PA ?? '',
          AR: selectedPositional.AR ?? '',
          Skills: selectedPositional.Skills.join(', '),
          Primary: selectedPositional.Primary ?? '',
          Secondary: selectedPositional.Secondary ?? '',
          Cost: selectedPositional.Cost ?? '',
        }
      };
      hiredCount++;
    }

    if (hiredCount > 0) {
      const hiredCost = parsePlayerCost(selectedPositional.Cost) * hiredCount;
      if (!team.isRegistered) {
        const newTreasury = (team.treasury ?? treasury) - hiredCost;
        onChange({ ...team, players: updatedPlayers, treasury: newTreasury });
      } else {
        onChange({ ...team, players: updatedPlayers });
      }
    }
  };

  const handleFirePlayer = (index: number) => {
    const updatedPlayers = [...team.players];
    const firedCost = parsePlayerCost(updatedPlayers[index].stats?.Cost);
    updatedPlayers[index] = {
      number: updatedPlayers[index].number,
      name: '',
      position: '',
      stats: undefined
    };
    if (!team.isRegistered) {
      const newTreasury = (team.treasury ?? treasury) + firedCost;
      onChange({ ...team, players: updatedPlayers, treasury: newTreasury });
    } else {
      onChange({ ...team, players: updatedPlayers });
    }
  };

  const handleMovePlayer = (index: number, direction: number) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= 16) return;

    const updatedPlayers = [...team.players];
    // Swap the players
    const temp = updatedPlayers[index];
    updatedPlayers[index] = updatedPlayers[newIndex];
    updatedPlayers[newIndex] = temp;

    // Fix their index (just for array stability if we ever rely on index, though numbers travel with them)
    // We no longer auto-reset the jersey number to index+1.

    onChange({ ...team, players: updatedPlayers });
  };


  const handlePlayerUpdate = (
    index: number, 
    name: string, 
    number: number, 
    customPosition?: string, 
    customKeywords?: string
  ) => {
    const updatedPlayers = [...team.players];
    const player = { ...updatedPlayers[index] };
    player.name = name;
    player.number = number;
    
    if (customPosition !== undefined) {
      const cleanedPosition = customPosition.trim();
      const cleanedKeywords = customKeywords ? customKeywords.trim() : '';
      player.position = cleanedKeywords ? `${cleanedPosition} (${cleanedKeywords})` : cleanedPosition;
      player.isUnmatched = false;
    }
    
    updatedPlayers[index] = player;
    onChange({ ...team, players: updatedPlayers });
  };

  const handleStaffCountChange = (name: string, count: string) => {
    if (team.isRegistered) return;
    const num = parseInt(count, 10);
    if (isNaN(num)) return;
    const prev = staffCounts[name] || 0;
    const delta = num - prev;
    const costPer = teamData?.Staff?.find((s) => s.name === name)?.cost || 0;
    const newTreasury = (team.treasury ?? treasury) - (delta * costPer);
    onChange({ ...team, staffCounts: { ...staffCounts, [name]: num }, treasury: newTreasury });
  };

  const handleInducementCountChange = (name: string, count: string) => {
    if (team.isRegistered) return;
    const num = parseInt(count, 10);
    if (isNaN(num)) return;
    const prev = inducementCounts[name] || 0;
    const delta = num - prev;
    const costPer = teamData?.Inducements?.find((i) => i.name === name)?.cost || 0;
    const newTreasury = (team.treasury ?? treasury) - (delta * costPer);
    onChange({ ...team, inducementCounts: { ...inducementCounts, [name]: num }, treasury: newTreasury });
  };

  const handleStarPlayerSelection = (name: string) => {
    if (team.isRegistered) return;
    const isCurrentlySelected = selectedStarPlayers[name] || false;
    if (!isCurrentlySelected && currentRosterCount >= 16) {
      setErrorMessage("Roster is full. Cannot hire Star Player.");
      return;
    }
    const starCost = eligibleStarPlayers.find((sp) => sp.name === name)?.Cost || 0;
    if (!isCurrentlySelected && (team.treasury ?? treasury) < starCost) {
      setErrorMessage('Not enough treasury to draft this Star Player.');
      return;
    }
    const selections = { ...selectedStarPlayers };
    selections[name] = !selections[name];
    const newTreasury = !isCurrentlySelected
      ? (team.treasury ?? treasury) - starCost
      : (team.treasury ?? treasury) + starCost;
    onChange({ ...team, selectedStarPlayers: selections, treasury: newTreasury });
    setErrorMessage(null);
  };

  return (
    <div className="roster-page">
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {team.isRegistered && team.treasury === undefined && (
        <div style={{
          background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '6px',
          padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: '#92400e',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
        }}>
          <span>⚠️ <strong>Treasury &amp; staff counts not imported.</strong> TourPlay renders these as graphics which cannot be read — please set them manually in the Staff panel.</span>
          <button
            onClick={() => setIsTreasuryModalOpen(true)}
            style={{
              background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px',
              padding: '4px 12px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '12px'
            }}
          >
            Set Treasury
          </button>
        </div>
      )}

      <div className="roster-container">
        {/* Roster Table */}
        <div className="panel player-table">
          <div
            className="collapsible-header"
            onClick={() => setIsRosterCollapsed(!isRosterCollapsed)}
            style={{ margin: 0, marginBottom: isRosterCollapsed ? '0' : '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          >
            <h3 style={{ margin: 0 }}>Roster</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                background: currentRosterCount >= 11 ? '#dcfce7' : '#f1f5f9',
                color: currentRosterCount >= 11 ? '#166534' : '#64748b',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                border: `1px solid ${currentRosterCount >= 11 ? '#bbf7d0' : '#cbd5e1'}`
              }}>
                {currentRosterCount}/16
              </span>
              <span className="collapsible-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
                {isRosterCollapsed ? '▼' : '▲'}
              </span>
            </div>
          </div>

          {!isRosterCollapsed && (
            <>
          {positionals.length > 0 && (
            <TeamMakeup
              positionals={positionals}
              players={team.players}
              onBadgeClick={(pos) => {
                setSelectedPositional(pos);
                setModalMode('hire');
                setIsModalOpen(true);
              }}
            />
          )}
          <div className="table-scroll-wrapper">
            <table className="roster-table-el">
              <thead>
                <tr>
                  <th className="num-col">#</th>
                  <th className="position-col">Position</th>
                  <th>Player Name</th>
                  <th className="stat-col">MA</th>
                  <th className="stat-col">ST</th>
                  <th className="stat-col">AG</th>
                  <th className="stat-col">PA</th>
                  <th className="stat-col">AR</th>
                  <th className="skills-col">Skills</th>
                  <th className="cost-col">Cost</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const starsToRender = [...hiredStarPlayers];
                  return team.players.map((player, index) => {
                    if (!player.position && starsToRender.length > 0) {
                      const starPlayer = starsToRender.shift();
                      if (!starPlayer) return null;
                      return (
                        <tr key={`star-${index}`} style={{ backgroundColor: '#fffbe1' }}>
                          <td className="player-num-cell">
                            <span className="player-number" style={{ color: '#d97706', fontWeight: 'bold' }}>
                              ⭐
                            </span>
                          </td>
                          <td className="position-cell">
                            <div
                              className="clickable-position"
                              onClick={() => setSelectedStarPlayerForModal(starPlayer)}
                              style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                            >
                              <span className="position-main-text" style={{ fontWeight: 'bold', color: '#92400e' }}>
                                Star Player
                              </span>
                              <span className="position-keywords-sub" style={{ fontSize: '11px', color: '#d97706', marginTop: '2px', fontStyle: 'italic' }}>
                                {starPlayer.Keywords ? `(${starPlayer.Keywords})` : 'Click to view rules'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span style={{ fontWeight: '600' }}>{starPlayer.name}</span>
                          </td>
                          <td className="stat-col">{starPlayer.MA || '-'}</td>
                          <td className="stat-col">{starPlayer.ST || '-'}</td>
                          <td className="stat-col">{starPlayer.AG || '-'}</td>
                          <td className="stat-col">{starPlayer.PA || '-'}</td>
                          <td className="stat-col">{starPlayer.AR || '-'}</td>
                          <td className="skills-col" style={{ fontSize: '11px' }}>
                            {starPlayer.Skills ? starPlayer.Skills.join(', ') : ''}
                          </td>
                          <td className="cost-col">{starPlayer.Cost ? formatCost(starPlayer.Cost) : ''}</td>
                          <td className="actions-cell" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                title="Fire Star Player"
                                onClick={(e) => { e.stopPropagation(); handleStarPlayerSelection(starPlayer.name); }}
                                style={{
                                  cursor: 'pointer', background: '#fee2e2', border: '1px solid #fca5a5',
                                  color: '#dc2626', borderRadius: '4px', padding: '2px 6px', fontWeight: 'bold'
                                }}
                              >✖</button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <React.Fragment key={index}>
                        <tr>
                          <td className="player-num-cell">
                            <span className="player-number" style={{ color: player.position ? '#333' : '#aaa', fontWeight: player.position ? 'bold' : 'normal' }}>
                              {player.position ? (player.number || '') : (index + 1)}
                            </span>
                          </td>
                          <td className="position-cell">
                            <div className="select-container">
                              {player.position ? (
                                <div
                                  className="clickable-position"
                                  onClick={() => {
                                    const posData = {
                                      Position: player.position,
                                      ...player.stats,
                                      Skills: player.stats?.Skills ? player.stats.Skills.split(', ') : [],
                                      isUnmatched: player.isUnmatched
                                    };
                                    setSelectedPositional(posData);
                                    setSelectedPlayerIndex(index);
                                    setModalMode('view');
                                    setIsModalOpen(true);
                                  }}
                                  style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                                >
                                  <span className="position-main-text" style={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <span>{player.position.split(' (')[0]}</span>
                                    {player.isUnmatched && (
                                      <span style={{ 
                                        backgroundColor: '#fee2e2', 
                                        color: '#ef4444', 
                                        fontSize: '10px', 
                                        padding: '1px 6px', 
                                        borderRadius: '3px', 
                                        fontWeight: 'bold',
                                        border: '1px solid #fca5a5',
                                        display: 'inline-block'
                                      }}>
                                        ⚠️ Complete Details
                                      </span>
                                    )}
                                  </span>
                                  <span className="position-keywords-sub" style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                                    {getPositionKeywords(player.position)}
                                  </span>
                                </div>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic', fontSize: '13px' }}>Empty Slot</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {player.position ? (
                              <span style={{ fontWeight: '500' }}>{player.name}</span>
                            ) : null}
                          </td>
                          <td className="stat-col">{player.stats?.MA || ''}</td>
                          <td className="stat-col">{player.stats?.ST || ''}</td>
                          <td className="stat-col">{player.stats?.AG || ''}</td>
                          <td className="stat-col">{player.stats?.PA || ''}</td>
                          <td className="stat-col">{player.stats?.AR || ''}</td>
                          <td className="skills-col" style={{ fontSize: '11px' }}>{player.stats?.Skills || ''}</td>
                          <td className="cost-col">{player.stats?.Cost ? formatCost(player.stats.Cost) : ''}</td>
                          <td className="actions-cell" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {player.position && (
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  title="Move Up"
                                  onClick={() => handleMovePlayer(index, -1)}
                                  disabled={index === 0}
                                  style={{
                                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                                    background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 6px', opacity: index === 0 ? 0.3 : 1
                                  }}
                                >↑</button>
                                <button
                                  type="button"
                                  title="Move Down"
                                  onClick={() => handleMovePlayer(index, 1)}
                                  disabled={index === 15}
                                  style={{
                                    cursor: index === 15 ? 'not-allowed' : 'pointer',
                                    background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 6px', opacity: index === 15 ? 0.3 : 1
                                  }}
                                >↓</button>
                                <button
                                  type="button"
                                  title="Fire Player"
                                  onClick={() => handleFirePlayer(index)}
                                  style={{
                                    cursor: 'pointer', background: '#fee2e2', border: '1px solid #fca5a5',
                                    color: '#dc2626', borderRadius: '4px', padding: '2px 6px', fontWeight: 'bold'
                                  }}
                                >✖</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
            </>
          )}
        </div>

        {/* Details Panel */}
        <div className="panel details-panel">
          {teamData ? (
            <>
              {/* Staff Section */}
              <div className="collapsible-header" onClick={() => setIsStaffCollapsed(!isStaffCollapsed)}>
                <h3>Staff</h3>
                <span className="collapsible-icon">{isStaffCollapsed ? '▼' : '▲'}</span>
              </div>
              {!isStaffCollapsed && (
                teamData.Staff && teamData.Staff.length > 0 ? (
                  <div style={{ padding: '4px 0 12px 0' }}>
                    {/* Treasury Row — clickable like staff items */}
                    <button
                      onClick={() => setIsTreasuryModalOpen(true)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        width: '100%', padding: '10px 14px', marginBottom: '10px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>Treasury</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>Remaining gold</div>
                      </div>
                      <span style={{
                        fontWeight: 'bold', fontSize: '14px',
                        color: team.isRegistered && team.treasury === undefined ? '#f59e0b' : (treasury >= 0 ? '#16a34a' : '#dc2626'),
                        padding: '2px 10px',
                      }}>
                        {team.isRegistered && team.treasury === undefined ? '⚠ Not set' : `${treasury.toLocaleString()} gp`}
                      </span>
                    </button>

                    {/* Staff rows as clickable buttons */}
                    {teamData.Staff.map((staff) => {
                      const count = staffCounts[staff.name] || 0;
                      return (
                        <button
                          key={staff.name}
                          onClick={() => setStaffModalItem(staff)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            width: '100%', padding: '10px 14px', marginBottom: '6px',
                            background: count > 0 ? '#eff6ff' : '#fff',
                            border: `1px solid ${count > 0 ? '#bfdbfe' : '#e2e8f0'}`,
                            borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{staff.name}</div>
                            <div style={{ fontSize: '12px', color: '#94a3b8' }}>{staff.cost.toLocaleString()} gp each</div>
                          </div>
                          <span style={{
                            background: count > 0 ? '#2563eb' : '#e2e8f0',
                            color: count > 0 ? '#fff' : '#64748b',
                            borderRadius: '12px', padding: '2px 10px',
                            fontSize: '13px', fontWeight: 'bold', minWidth: '24px', textAlign: 'center',
                          }}>
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ padding: '0 12px' }}>No staff available.</p>
                )
              )}

              {/* Inducements Section */}
              <div
                className="collapsible-header"
                onClick={() => setIsInducementsCollapsed(!isInducementsCollapsed)}
                style={{ cursor: 'pointer' }}
              >
                <h3>Inducements</h3>
                <span className="collapsible-icon">{isInducementsCollapsed ? '▼' : '▲'}</span>
              </div>
              {!isInducementsCollapsed && (
                teamData.Inducements && teamData.Inducements.length > 0 ? (
                  <div style={{ padding: '4px 0 12px 0' }}>
                    {teamData.Inducements.map((inducement) => {
                      const count = rulesetMode === 'exhibition' ? (inducementCounts[inducement.name] || 0) : 0;
                      return (
                        <button
                          key={inducement.name}
                          onClick={() => setInducementModalItem(inducement)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            width: '100%', padding: '10px 14px', marginBottom: '6px',
                            background: count > 0 ? '#eff6ff' : '#fff',
                            border: `1px solid ${count > 0 ? '#bfdbfe' : '#e2e8f0'}`,
                            borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{inducement.name}</div>
                            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                              {inducement.cost ? `${Number(inducement.cost).toLocaleString()} gp each` : 'Variable cost'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {rulesetMode === 'exhibition' && !team.isRegistered ? (
                              <span style={{
                                background: count > 0 ? '#2563eb' : '#e2e8f0',
                                color: count > 0 ? '#fff' : '#64748b',
                                borderRadius: '12px', padding: '2px 10px',
                                fontSize: '13px', fontWeight: 'bold', minWidth: '24px', textAlign: 'center',
                              }}>
                                {count}
                              </span>
                            ) : (
                              <span style={{
                                background: count > 0 ? '#16a34a' : '#f1f5f9',
                                border: count > 0 ? '1px solid #15803d' : '1px solid #cbd5e1',
                                color: count > 0 ? 'white' : '#475569',
                                padding: count > 0 ? '2px 10px' : '2px 8px',
                                borderRadius: count > 0 ? '12px' : '4px',
                                fontSize: count > 0 ? '13px' : '11px',
                                fontWeight: 'bold'
                              }}>
                                {count > 0 ? count : 'Ref Only'}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ padding: '0 12px' }}>No inducements available.</p>
                )
              )}

              {/* Star Players Section */}
              <div
                className="collapsible-header"
                onClick={() => setIsStarPlayersCollapsed(!isStarPlayersCollapsed)}
                style={{ cursor: 'pointer' }}
              >
                <h3>Star Players</h3>
                <span className="collapsible-icon">{isStarPlayersCollapsed ? '▼' : '▲'}</span>
              </div>
              {!isStarPlayersCollapsed && (
                eligibleStarPlayers.length > 0 ? (
                  <div style={{ padding: '4px 0 12px 0' }}>
                    {eligibleStarPlayers.map((starPlayer: any) => {
                      const isSelected = selectedStarPlayers[starPlayer.name] || false;
                      const cost = typeof starPlayer.Cost === 'number' ? starPlayer.Cost : 0;
                      return (
                        <button
                          key={starPlayer.name}
                          onClick={() => setSelectedStarPlayerForModal(starPlayer)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            padding: '10px 14px',
                            marginBottom: '6px',
                            background: isSelected ? '#eff6ff' : '#fff',
                            border: `1px solid ${isSelected ? '#bfdbfe' : '#e2e8f0'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>
                              ⭐ {starPlayer.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                              {cost > 0 ? `${cost.toLocaleString()} gp` : 'Variable Cost'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', height: '100%' }}>
                            {rulesetMode === 'exhibition' && !team.isRegistered ? (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleStarPlayerSelection(starPlayer.name);
                                }}
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                              />
                            ) : (
                              <span style={{
                                background: isSelected ? '#16a34a' : '#f1f5f9',
                                border: isSelected ? '1px solid #15803d' : '1px solid #cbd5e1',
                                color: isSelected ? 'white' : '#475569',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 'bold'
                              }}>
                                {isSelected ? 'Hired' : 'Ref Only'}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ padding: '0 12px', color: '#64748b' }}>No eligible star players found for this team.</p>
                )
              )}

              {/* League Rules Info Notice */}
              {rulesetMode === 'league' && (
                <div style={{ backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', padding: '12px', marginTop: '16px', color: '#b78103', fontSize: '13px', lineHeight: '1.5' }}>
                  <strong>🔒 Inducements & Star Player Drafting Locked:</strong> Under 2025 League rules, Inducements and Star Players are purchased dynamically using petty cash or treasury during pre-game match setup, rather than in the starting team draft. However, you can click on any Inducement or Star Player above to view their reference rules card. Switch the ruleset to <em>Tournament</em> to enable them for drafting.
                </div>
              )}
            </>
          ) : (
            <p>Please select a team to view details.</p>
          )}
        </div>
      </div>

      {isModalOpen && selectedPositional && (
        <PlayerCardModal
          positional={selectedPositional}
          playerName={selectedPlayerIndex !== null ? team.players[selectedPlayerIndex].name : ''}
          playerNumber={selectedPlayerIndex !== null ? team.players[selectedPlayerIndex].number : null}
          onClose={() => setIsModalOpen(false)}
          onHire={handleHirePlayer}
          onFire={() => selectedPlayerIndex !== null && handleFirePlayer(selectedPlayerIndex)}
          onUpdatePlayer={(name, number, customPosition, customKeywords) => {
            if (selectedPlayerIndex !== null) {
              handlePlayerUpdate(selectedPlayerIndex, name, number, customPosition, customKeywords);
            }
          }}
          canHire={!isPositionFull(selectedPositional.Position, -1)}
          isRosterFull={team.players.filter(p => p.position).length >= 16}
          mode={modalMode}
          currentCount={getPositionCount(selectedPositional.Position)}
          maxLimit={getMaxLimit(selectedPositional.Qty)}
          isRegistered={team.isRegistered}
          treasury={treasury}
          totalHiredCount={team.players.filter(p => p.position).length}
        />
      )}
      {isTreasuryModalOpen && (
        <TreasuryModal
          treasury={treasury}
          onClose={() => setIsTreasuryModalOpen(false)}
          onUpdate={(newValue) => onChange({ ...team, treasury: newValue })}
        />
      )}
      {staffModalItem && (
        <StaffModal
          staffItem={staffModalItem}
          currentCount={staffCounts[staffModalItem.name] || 0}
          remainingGold={treasury}
          onClose={() => setStaffModalItem(null)}
          onUpdate={(newCount) => {
            handleStaffCountChange(staffModalItem.name, String(newCount));
          }}
        />
      )}
      {inducementModalItem && (
        <StaffModal
          staffItem={{ ...inducementModalItem, cost: inducementModalItem.cost ?? 0 }}
          currentCount={inducementCounts[inducementModalItem.name] || 0}
          remainingGold={treasury}
          isReadOnly={rulesetMode === 'league' || team.isRegistered}
          onClose={() => setInducementModalItem(null)}
          onUpdate={(newCount) => {
            handleInducementCountChange(inducementModalItem.name, String(newCount));
          }}
        />
      )}
      {selectedStarPlayerForModal && (
        <StarPlayerModal
          player={selectedStarPlayerForModal}
          isSelected={selectedStarPlayers[selectedStarPlayerForModal.name] || false}
          onToggleSelect={() => handleStarPlayerSelection(selectedStarPlayerForModal.name)}
          onClose={() => setSelectedStarPlayerForModal(null)}
          rulesetMode={rulesetMode}
          isRegistered={team.isRegistered}
        />
      )}
    </div>
  );
}
